import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { SenderIdItemWithOpaList } from '../interfaces/sender-id.interface';
import { ProfileService } from './profile.service';
import { SenderIdAuthorisationItem } from '../interfaces/sender-id-authorisation-item.interface';
import { SenderIdPendingAuthorisationRequest } from '../interfaces/sender-id-pending-authorisation-request.interface';
import { SenderIdPendingAuthorisationItem } from '../interfaces/sender-id-pending-authorisation-item.interface';
import { SenderIdAllOpasRequest } from '../interfaces/sender-id-all-opas-request.interface';
import { SenderIdsRequest } from '../interfaces/sender-ids-request.interface';
import { AvailableSidoItem } from '../interfaces/available-sido-item';
import { OpaItemWithSenderIdList } from '../interfaces/opa-item-with-sender-id-list.interface';
import { OpaAuthorisationItem } from '../interfaces/opa-authorisation-item.interface';
import { OPASenderIdAuthorisedItem } from '../interfaces/opa-sender-id-authorised-item.interface';
import { Observable } from 'rxjs';
import { StatusChange } from '../interfaces/status-change.interface';
import { AllPendingSenderIdAuthorisationItem } from '../interfaces/all-pending-sender-id-authorisation-item.interface';
import { ThirdPartyAssignmentItem } from '../interfaces/third-party-assignement-item.interface';
import { ThirdPartyAssignmentDataList } from '../interfaces/third-party-assignement-data-list.interface';
import { HttpContextTokens, HttpLoggingOptions } from 'common';
import { CreateSenderIdResponse } from '../interfaces/create-sender-id-response.interface';
import { RtpOpaAssignment } from '../interfaces/rtp-opa-assignment.interface';

const sharedUrl = '/senderidregistry/account';

@Injectable({
  providedIn: 'root',
})
export class AccountApiService {
  private http = inject(HttpClient);
  private profileService = inject(ProfileService);

  private httpContext: HttpContext = null;

  constructor() {
    this.initialiseHttpContext();
  }

  private initialiseHttpContext(): void {
    if (this.httpContext === null) {
      this.httpContext = new HttpContext();

      var httpLoggingOptions = new HttpLoggingOptions();

      //The order creation process uses post calls to build up the information on the API/DB
      //we don't want to see a "success" popup for each of those backgroud operations
      httpLoggingOptions.setDisplaySuccess('POST', false);

      this.httpContext.set(HttpContextTokens.options, httpLoggingOptions);
    }
  }

  getAllPendingAuthorisationListCount$() {
    return this.http.get<number>(`${sharedUrl}/${this.profileService.shortAccountId}/senderid/pending-authorisation/count`);
  }

  getAllPendingAuthorisationList$() {
    return this.http.get<AllPendingSenderIdAuthorisationItem[]>(`${sharedUrl}/${this.profileService.shortAccountId}/senderid/pending-authorisation`);
  }

  addAllSenderIdPendingAuthorisations$(data: SenderIdPendingAuthorisationRequest) {
    return this.http.post<SenderIdPendingAuthorisationRequest>(`${sharedUrl}/${this.profileService.shortAccountId}/senderid/pending-authorisation`, data, { context: this.httpContext });
  }

  getSenderIdPendingList$(rejected = false, withdrawn = false) {
    return this.http.get<SenderIdItemWithOpaList[]>(`${sharedUrl}/${this.profileService.shortAccountId}/senderid/list/${rejected}/${withdrawn}`);
  }

  getSenderIdPendingAuthorisationList$(senderId: number) {
    return this.http.get<SenderIdPendingAuthorisationItem[]>(`${sharedUrl}/${this.profileService.shortAccountId}/senderid/${senderId}/pending-authorisation`);
  }

  addSenderIdPendingAuthorisations$(senderId: number, data: SenderIdPendingAuthorisationRequest) {
    return this.http.post<SenderIdPendingAuthorisationRequest>(`${sharedUrl}/${this.profileService.shortAccountId}/senderid/${senderId}/pending-authorisation`, data, { context: this.httpContext });
  }

  getSenderIdAllOpas$(senderId: number) {
    return this.http.get<SenderIdAuthorisationItem[]>(`${sharedUrl}/${this.profileService.shortAccountId}/senderid/${senderId}/all-opas`);
  }

  addSenderIdAllOpasList$(senderId: number, data: SenderIdAllOpasRequest) {
    return this.http.post<SenderIdAllOpasRequest>(`${sharedUrl}/${this.profileService.shortAccountId}/senderid/${senderId}/all-opas`, data, { context: this.httpContext });
  }

  addSenderIds$(data: SenderIdsRequest) {
    return this.http.post<CreateSenderIdResponse>(`${sharedUrl}/${this.profileService.shortAccountId}/senderid`, data, { context: this.httpContext });
  }

  setSenderIdStatus$(data: StatusChange) {
    return this.http.post(`${sharedUrl}/${this.profileService.shortAccountId}/senderid/status/update`, data, { context: this.httpContext });
  }

  getAvailableSidoList$(text = '') {
    return this.http.get<AvailableSidoItem[]>(`${sharedUrl}/${this.profileService.shortAccountId}/senderid/sido-list`, {
      params: {
        text,
      },
    });
  }

  getOpasList$() {
    return this.http.get<OpaItemWithSenderIdList[]>(`${sharedUrl}/${this.profileService.shortAccountId}/opa/list`);
  }

  getOpaSenderIds$(opaId: number) {
    return this.http.get<OpaAuthorisationItem[]>(`${sharedUrl}/${this.profileService.shortAccountId}/opa/${opaId}/all-senderids`);
  }

  addOpaSenderIds$(opaId: number, data) {
    return this.http.post(`${sharedUrl}/${this.profileService.shortAccountId}/opa/${opaId}/all-senderids`, data, { context: this.httpContext });
  }

  getOpaAuthorisedSenderIds$() {
    return this.http.get<OPASenderIdAuthorisedItem[]>(`${sharedUrl}/${this.profileService.shortAccountId}/opa/senderdid-authorised/list`);
  }

  getThirdParties$(senderId?: number) {
    return senderId
      ? this.http.get<ThirdPartyAssignmentItem[]>(`${sharedUrl}/${this.profileService.shortAccountId}/senderid/${senderId}/third-parties/available`)
      : this.http.get<ThirdPartyAssignmentItem[]>(`${sharedUrl}/${this.profileService.shortAccountId}/senderid/third-parties/available`)
  }

  updateThirdParties$(senderId: number, data: ThirdPartyAssignmentDataList) {
    return this.http.post(`${sharedUrl}/${this.profileService.shortAccountId}/senderid/${senderId}/third-parties`, data, { context: this.httpContext });
  }

  getOpaAuthorisedSenderIdsFile$(extension: 'csv' | 'json'): Observable<Blob> {
    return this.http.get(`${sharedUrl}/${this.profileService.shortAccountId}/opa/senderdid-opa-authorised/download/${extension}`, { responseType: 'blob' });
  }

  getRegisteredPaFile$(extension: 'csv' | 'json'): Observable<Blob> {
    return this.http.get(`${sharedUrl}/${this.profileService.shortAccountId}/opa/registered-pa/download/${extension}`, { responseType: 'blob' });
  }

  getRegisteredSenderIdsFile$(extension: 'csv' | 'json'): Observable<Blob> {
    return this.http.get(`${sharedUrl}/${this.profileService.shortAccountId}/opa/registered-sender-id/download/${extension}`, { responseType: 'blob' });
  }

  getTokenGeneration(): Observable<string> {
    const headers = new HttpHeaders().set('Content-Type', 'text/plain; charset=utf-8');

    return this.http.get<string>(`${sharedUrl}/${this.profileService.shortAccountId}/opa/generate-token`, { headers, responseType: 'text' as 'json' });
  }

  getApiDocumentation(): Observable<Blob> {
    return this.http.get(`${sharedUrl}/${this.profileService.shortAccountId}/opa/api-documentation/download`, { responseType: 'blob' });
  }

  getRtpOpaAssignmentList$(): Observable<RtpOpaAssignment[]> {
    return this.http.get<RtpOpaAssignment[]>(`${sharedUrl}/${this.profileService.shortAccountId}/registration/rtp-opa-assignment/list`);
  }
}
